import React, { useState, useRef, useEffect } from "react";
import { NotificationSettings } from "./NotificationSettings";
import { NotificationHeader } from "./NotificationHeader";
import { NotificationToolbar } from "./NotificationToolbar";
import { NotificationList } from "./NotificationList";
import {
  updateNotificationStatus,
  markAllNotificationsAsRead,
  fetchNotificationPreferences,
} from "./NotificationsApi";
import {
  Notification,
  NotificationStatus,
  UpdateNotificationBody,
} from "target_wasm";

interface NotificationDropdownProps {
  notifications: Notification[];
  isVisible: boolean;
  onClose?: () => void;
  onNotificationClick?: (notificationId: string) => void;
  onNotificationUpdate?: () => void;
  triggerRef?: React.RefObject<HTMLElement>;
  sidebarWidth: string;
  onOpenSettings?: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  isVisible,
  onClose,
  onNotificationClick,
  onNotificationUpdate,
  triggerRef,
  sidebarWidth,
  onOpenSettings,
}) => {
  const [activeFilter, setActiveFilter] = useState<"all" | "unread">("all");
  const [currentView, setCurrentView] = useState<"notifications" | "settings">(
    "notifications",
  );
  const [filterCategories, setFilterCategories] = useState({
    all: false,
    requests: false,
    updates: false,
  });
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [justMarkedAsSeenId, setJustMarkedAsSeenId] = useState<string | null>(
    null,
  );
  const [settingsInitialPreferences, setSettingsInitialPreferences] = useState<
    { inApp: boolean; email: boolean } | undefined
  >(undefined);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleNotificationClick = async (notification: Notification) => {
    if (notification.status_js() === NotificationStatus.Pending) {
      try {
        const updateRequest = UpdateNotificationBody.new_js(
          notification.notification_id_js(),
          NotificationStatus.Seen,
        );
        await updateNotificationStatus(updateRequest);
        setJustMarkedAsSeenId(notification.notification_id_js());

        setTimeout(() => {
          setJustMarkedAsSeenId(null);
        }, 4000);

        onNotificationUpdate?.();
      } catch (error) {
        console.error("Failed to update notification status:", error);
      }
    }
    onNotificationClick?.(notification.notification_id_js());
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      const clickedInsideDropdown = dropdownRef.current?.contains(targetNode);
      const clickedOnTrigger = triggerRef?.current?.contains(targetNode);
      if (!clickedInsideDropdown && !clickedOnTrigger) {
        onClose?.();
      }
    };

    if (isVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isVisible, onClose, triggerRef]);

  useEffect(() => {
    const node = dropdownRef.current;
    if (!node) return;
    const stop = (e: MouseEvent) => e.stopPropagation();
    node.addEventListener("mousedown", stop);
    return () => {
      node.removeEventListener("mousedown", stop);
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      const hasUnread = notifications.some(
        (n) => n.status_js() === NotificationStatus.Pending,
      );
      setActiveFilter(hasUnread ? "unread" : "all");
    }
  }, [isVisible]);

  if (!isVisible) return null;

  const unreadNotifications = notifications.filter(
    (notification) => notification.status_js() === NotificationStatus.Pending,
  );
  const unreadCount = unreadNotifications.length;

  const handleCategoryFilterChange = (
    category: "all" | "requests" | "updates",
  ) => {
    if (category === "all") {
      setFilterCategories((prev) => {
        const isAllSelected = prev.requests && prev.updates;
        const nextVal = !isAllSelected;
        return {
          all: nextVal,
          requests: nextVal,
          updates: nextVal,
        };
      });
      return;
    }

    setFilterCategories((prev) => {
      const nextRequests =
        category === "requests" ? !prev.requests : prev.requests;
      const nextUpdates = category === "updates" ? !prev.updates : prev.updates;
      return {
        all: nextRequests && nextUpdates,
        requests: nextRequests,
        updates: nextUpdates,
      };
    });
  };

  const handleMarkAllAsRead = async () => {
    if (unreadCount === 0) return;

    setMarkingAllAsRead(true);
    try {
      await markAllNotificationsAsRead();
      onNotificationUpdate?.();
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    } finally {
      setMarkingAllAsRead(false);
    }
  };

  const renderSettingsView = () => {
    const clientId = localStorage.getItem("clientID");

    if (!clientId) {
      return (
        <div className="space-y-4">
          <div className="text-error">Failed to load Settings.</div>
        </div>
      );
    }

    return (
      <NotificationSettings
        clientId={clientId}
        initialPreferences={settingsInitialPreferences}
      />
    );
  };

  return (
    <div
      ref={dropdownRef}
      className="notification-dropdown sidebar-dropdown bg-bg-subtle"
      style={{
        top: triggerRef?.current?.getBoundingClientRect().top || 0,
        left: `calc(${sidebarWidth} + 8px)`,
      }}
    >
      {currentView === "settings" && (
        <NotificationHeader
          currentView={currentView}
          onOpenSettings={async () => {}}
          onBackToNotifications={() => setCurrentView("notifications")}
        />
      )}

      {currentView === "notifications" ? (
        <>
          <NotificationToolbar
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
            unreadCount={unreadCount}
            onMarkAllAsRead={handleMarkAllAsRead}
            markingAllAsRead={markingAllAsRead}
            filterCategories={filterCategories}
            showFilterDropdown={showFilterDropdown}
            onToggleDropdown={() => setShowFilterDropdown((prev) => !prev)}
            onCategoryFilterChange={handleCategoryFilterChange}
            onCloseDropdown={() => setShowFilterDropdown(false)}
            onOpenSettings={
              onOpenSettings
                ? () => {
                    onClose?.();
                    onOpenSettings();
                  }
                : async () => {
                    try {
                      const prefs = await fetchNotificationPreferences();
                      setSettingsInitialPreferences({
                        inApp: prefs.enabled,
                        email: prefs.in_email_notification,
                      });
                      setCurrentView("settings");
                    } catch {
                      // Opening settings is best-effort; leave the view alone.
                    }
                  }
            }
          />

          <NotificationList
            notifications={notifications}
            activeFilter={activeFilter}
            filterCategories={filterCategories}
            justMarkedAsSeenId={justMarkedAsSeenId}
            onItemClick={handleNotificationClick}
            onNotificationUpdate={onNotificationUpdate}
          />
        </>
      ) : (
        <div className="pl-8">{renderSettingsView()}</div>
      )}
    </div>
  );
};
